import { useCallback, useEffect, useState } from "react"
import { api } from "@/shared/api-client"
import { describeDevice, devicePublicKey, deviceSign } from "@/shared/deviceKey"

interface Activated {
  access_token: string
  username: string
  role: string
}

interface Props {
  onActive: (a: Activated) => void
  onLogout: () => void
}

const btn =
  "w-full py-2.5 px-4 rounded-lg bg-gradient-to-r from-[var(--hh-accent-from)] to-[var(--hh-accent-to)] hover:brightness-110 text-white text-sm font-medium disabled:opacity-60"

/**
 * Конец входа для аккаунтов из регистрации: пароль + ключ ЭТОГО устройства.
 * Нет одобренного устройства → создаётся заявка владельцу, пользователь видит только «ожидаем подтверждения».
 */
export function DeviceGate({ onActive, onLogout }: Props) {
  const [state, setState] = useState<"checking" | "pending" | "rejected" | "error">("checking")
  const [msg, setMsg] = useState("")

  const attempt = useCallback(async () => {
    setState("checking")
    try {
      const spki = await devicePublicKey()
      const d = describeDevice()
      const proof = async () => {
        const c = await api.post<{ challenge_id: string; challenge: string }>("/devices/request/begin", {})
        return { challenge_id: c.challenge_id, spki, signature: await deviceSign(c.challenge), type: d.type, browser: d.browser }
      }
      try {
        onActive(await api.post<Activated>("/devices/activate", await proof()))
        return
      } catch (e) {
        if (!(e instanceof Error) || !e.message.includes("device_unknown")) throw e
      }
      await api.post("/devices/request/finish", await proof())
      const me = await api.get<{ device_state: string }>("/registration/me")
      setState(me.device_state === "rejected" ? "rejected" : "pending")
    } catch (e) {
      setMsg(e instanceof Error ? e.message : "")
      setState("error")
    }
  }, [onActive])

  useEffect(() => {
    void attempt()
  }, [attempt])

  return (
    <div className="min-h-[100dvh] flex items-center justify-center bg-[#020617] px-4">
      <div className="w-full max-w-sm rounded-2xl border border-white/[8%] bg-white/[3%] p-8 space-y-4 text-center">
        <h1 className="text-lg font-semibold text-zinc-100">Регистрация завершена</h1>
        {state === "checking" && <p className="text-sm text-zinc-400">Проверяю устройство…</p>}
        {state === "pending" && (
          <p className="text-sm text-zinc-300">Регистрация завершена. Ожидаем подтверждения администратора.</p>
        )}
        {state === "rejected" && <p className="text-sm text-rose-400">Заявка отклонена администратором.</p>}
        {state === "error" && (
          <p className="text-sm text-rose-400">
            Не удалось проверить устройство{msg ? `: ${msg}` : ""}. Откройте Джарвиса по адресу https:// в современном браузере.
          </p>
        )}
        <div className="space-y-2 pt-2">
          <button className={btn} onClick={() => void attempt()} disabled={state === "checking"}>Проверить ещё раз</button>
          <button className="text-xs text-zinc-500 hover:text-zinc-300" onClick={onLogout}>Выйти</button>
        </div>
      </div>
    </div>
  )
}
